import { useState, useEffect } from "react";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { WhatsappLogo, Warning } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";

const detectTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
};
const allZones = () => {
  try { return Intl.supportedValuesOf("timeZone"); } catch { return ["UTC"]; }
};

const BRIEF_SECTIONS = [
  ["priority", "brief.sec_priority", "⚡ Top 5 priorities (with reasons)"],
  ["vips", "brief.sec_vips", "👑 VIP unread first"], ["awaiting", "brief.sec_awaiting", "⏳ Awaiting replies"], ["followups", "brief.sec_followups", "🔔 Follow-ups due"],
  ["calendar", "brief.sec_calendar", "📅 Today's meetings"], ["attachments", "brief.sec_attachments", "📎 New attachments"], ["unread", "brief.sec_unread", "📬 Unread summary"],
];
const DEFAULT_BRIEF = { enabled: false, time: "08:00", include: { priority: false, vips: true, awaiting: true, followups: true, calendar: true, attachments: true, unread: true } };

const SharedAlertToggles = () => {
  const [items, setItems] = useState([]);
  const [me, setMe] = useState("");
  const load = () => api.get("/shared-inboxes").then((r) => setItems(r.data.shared_inboxes || [])).catch(() => {});
  useEffect(() => { load(); api.get("/auth/me").then((r) => setMe(r.data.id || r.data.user?.id || "")).catch(() => {}); }, []);
  if (items.length === 0) return null;
  const toggle = async (s, enabled) => {
    try {
      await api.put(`/shared-inboxes/${s.id}/notifications`, { enabled });
      toast.success(enabled ? t("si.alerts_on", "Alerts on for {name}", { name: s.name }) : t("si.alerts_off", "Alerts muted for {name}", { name: s.name }));
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div data-testid="si-alert-toggles" className="mt-4 border-t border-gray-100 pt-3">
      <p className="text-xs font-semibold text-gray-700 mb-2">{t("si.alerts_title", "Shared inbox alerts")}</p>
      {items.map((s) => {
        const muted = (s.muted_user_ids || []).includes(me);
        return (
          <label key={s.id} className="flex items-center justify-between py-1.5 text-sm text-gray-700">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color || "#2563eb" }} />{s.name}
            </span>
            <input data-testid={`si-alert-toggle-${s.id}`} type="checkbox" checked={!muted}
              onChange={(e) => toggle(s, e.target.checked)} className="w-4 h-4 accent-green-600" />
          </label>
        );
      })}
      <p className="text-[10px] text-gray-400 mt-1">{t("si.alerts_note", "Muting stops WhatsApp rule/VIP alerts for that shared inbox. Your morning brief still includes it.")}</p>
    </div>
  );
};

export const WhatsAppCard = () => {
  const [st, setSt] = useState(null);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [ack, setAck] = useState(false);
  const [times, setTimes] = useState([]);
  const [urgent, setUrgent] = useState(true);
  const [tz, setTz] = useState(detectTz());
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(DEFAULT_BRIEF);
  const [vipAlerts, setVipAlerts] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const zones = allZones();

  const load = () => api.get("/whatsapp/settings").then((r) => {
    setSt(r.data); setTimes(r.data.summary_times || []); setUrgent(r.data.urgent_enabled !== false);
    setTz(r.data.tz || detectTz());
    if (!r.data.tz && r.data.link?.verified) {
      api.put("/whatsapp/settings", { tz: detectTz() }).catch(() => {});
    }
    setBrief(r.data.brief || DEFAULT_BRIEF);
    setVipAlerts(!!r.data.vip_alerts);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendCode = async () => {
    setBusy(true);
    try { await api.post("/whatsapp/link", { phone }); setCodeSent(true); toast.success(t("wa.code_sent", "Code sent to your WhatsApp")); }
    catch (e) { toast.error(errDetail(e)); }
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post("/whatsapp/verify", { code }); toast.success(t("wa.linked_toast", "WhatsApp linked!")); setCodeSent(false); setCode(""); load(); }
    catch (e) { toast.error(errDetail(e)); }
    setBusy(false);
  };
  const unlink = async () => {
    if (!window.confirm(t("wa.unlink_confirm", "Unlink your WhatsApp?"))) return;
    await api.delete("/whatsapp/link"); toast.success(t("wa.unlinked", "Unlinked")); load();
  };
  const saveSettings = async (tm, u, z) => {
    try { await api.put("/whatsapp/settings", { summary_times: tm, urgent_enabled: u, tz: z }); toast.success(t("wa.saved", "WhatsApp settings saved")); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const setTime = (i, v) => { const t = [...times]; t[i] = v; setTimes(t); };
  const setCount = (n) => {
    const t = [...times].slice(0, n);
    while (t.length < n) t.push("08:00");
    setTimes(t); saveSettings(t, urgent, tz);
  };
  const saveBrief = async (b) => {
    setBrief(b);
    try { await api.put("/whatsapp/settings", { brief: b, tz }); toast.success(t("brief.saved", "Morning Brief settings saved")); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const saveVip = async (v) => {
    setVipAlerts(v);
    try { await api.put("/whatsapp/settings", { vip_alerts: v }); toast.success(v ? t("wa.vip_on", "VIP instant alerts ON") : t("wa.vip_off", "VIP instant alerts OFF")); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const preview = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/whatsapp/brief/preview");
      setPreviewText(data.text);
      if (data.sent) toast.success(t("brief.preview_sent", "Preview sent to your WhatsApp ✓"));
      else toast.info(t("brief.preview_not_sent", "Preview generated below (not sent — WhatsApp unavailable)"));
    } catch (e) { toast.error(errDetail(e)); }
    setPreviewBusy(false);
  };

  const tzLabel = tz.replace(/_/g, " ");
  const linked = st?.link?.verified;
  return (
    <div data-testid="whatsapp-card" className="bg-white border border-gray-200 rounded-md p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <WhatsappLogo size={20} className="text-green-600" />
        <h3 className="font-heading font-bold text-gray-900">{t("wa.title", "WhatsApp")}</h3>
        {linked && <span data-testid="wa-linked-badge" className="text-[10px] font-bold bg-green-100 text-green-700 rounded-full px-2 py-0.5">✅ LINKED · +{st.link.phone}</span>}
      </div>
      <p className="text-xs text-gray-500 mb-4">{t("wa.desc", "Get inbox summaries and urgent alerts, and talk to your email agent from WhatsApp (text or voice notes).")}</p>
      {!linked ? (
        <div className="space-y-3">
          <div data-testid="wa-access-notice" className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-md p-3">
            <Warning size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <span className="font-semibold block mb-0.5">{t("wa.notice_title", "Full access notice")}</span>
              {t("wa.notice_p1", "Linking WhatsApp gives it the")} <b>{t("wa.notice_bold", "same power as this web app")}</b>{t("wa.notice_p2", ": read, search, send and modify your email. If your phone is lost or unlocked, whoever holds it can use your mailbox. You can unlink instantly from here anytime.")}
              <label className="flex items-center gap-2 mt-2 font-semibold text-amber-900 cursor-pointer">
                <input data-testid="wa-ack-checkbox" type="checkbox" checked={ack}
                  onChange={(e) => setAck(e.target.checked)} className="w-4 h-4 accent-amber-600" />
                {t("wa.accept", "I understand and accept")}
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <input data-testid="wa-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+31 6 12345678" className="border border-gray-300 rounded-md px-3 py-2 text-sm w-44" />
            <button data-testid="wa-send-code-btn" onClick={sendCode} disabled={busy || !phone.trim() || !ack}
              className="text-sm font-semibold bg-green-600 text-white rounded-md px-3 py-2 disabled:opacity-50">{t("wa.send_code", "Send code")}</button>
            {codeSent && (<>
              <input data-testid="wa-code-input" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder={t("wa.code_ph", "6-digit code")} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-32" />
              <button data-testid="wa-verify-btn" onClick={verify} disabled={busy || code.length < 6 || !ack}
                className="text-sm font-semibold bg-gray-900 text-white rounded-md px-3 py-2 disabled:opacity-50">{t("wa.verify", "Verify")}</button>
            </>)}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{t("wa.digest2", "Extra mini-summaries (optional)")}</div>
            <p className="text-[11px] text-gray-400 mb-1.5">{t("wa.digest_note", "Short unread-count digests during the day. Your main morning report is the Morning Brief below.")}</p>
            <div className="flex items-center gap-2 flex-wrap">
              {[0, 1, 2, 3].map((n) => (
                <button key={n} data-testid={`wa-summary-count-${n}`} onClick={() => setCount(n)}
                  className={`text-xs font-semibold rounded-full px-3 py-1 ${times.length === n ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600"}`}>
                  {n === 0 ? t("wa.off", "Off") : t("wa.per_day", "{n}× / day", { n })}</button>
              ))}
              {times.map((tm, i) => (
                <input key={i} data-testid={`wa-summary-time-${i}`} type="time" value={tm}
                  onChange={(e) => setTime(i, e.target.value)} onBlur={() => saveSettings(times, urgent, tz)}
                  className="border border-gray-300 rounded px-2 py-1 text-xs" />
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{t("wa.tz", "Timezone")}</div>
            <select data-testid="wa-tz-select" value={tz}
              onChange={(e) => { setTz(e.target.value); saveSettings(times, urgent, e.target.value); }}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-xs max-w-[280px] bg-white">
              {!zones.includes(tz) && <option value={tz}>{tzLabel}</option>}
              {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
            </select>
            {times.length > 0 && (
              <p data-testid="wa-tz-caption" className="text-[11px] text-gray-400 mt-1">
                {t("wa.summary_at", "Your summary arrives at {times} — {tz} time", { times: times.join(", "), tz: tzLabel })}
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input data-testid="wa-urgent-toggle" type="checkbox" checked={urgent}
              onChange={(e) => { setUrgent(e.target.checked); saveSettings(times, e.target.checked, tz); }} className="w-4 h-4 accent-green-600" />
            {t("wa.urgent", "Urgent rule alerts on WhatsApp")}
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input data-testid="wa-priority-alerts-toggle" type="checkbox" checked={!!st?.priority_alerts}
              onChange={async (e) => { const v = e.target.checked; setSt({ ...st, priority_alerts: v }); try { await api.put("/whatsapp/settings", { priority_alerts: v }); toast.success(t("wa.saved", "WhatsApp settings saved")); } catch (err) { toast.error(errDetail(err)); } }} className="w-4 h-4 accent-green-600" />
            {t("wa.priority_alerts", "⚡ ACT NOW alerts (once per conversation)")}
          </label>
          <p className="text-xs text-gray-400">{t("wa.tip", "Tip: create a rule like \"emails from microsoft.com → WhatsApp alert\" — or just ask the agent.")}</p>
          <button data-testid="wa-unlink-btn" onClick={unlink} className="text-xs text-red-600 underline">{t("wa.unlink", "Unlink WhatsApp")}</button>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100" data-testid="wa-brief-section">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-500 uppercase">🌅 {t("brief.title", "Morning Brief").replace("🌅 ", "")} — {t("brief.main_note", "your main daily report")}</span>
          <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer text-gray-600">
            <input data-testid="wa-brief-toggle" type="checkbox" checked={brief.enabled}
              onChange={(e) => saveBrief({ ...brief, enabled: e.target.checked })} className="w-4 h-4 accent-green-600" />
            {brief.enabled ? "ON" : "OFF"}
          </label>
        </div>
        <p className="text-[11px] text-gray-400 mb-2">{t("brief.desc", "One WhatsApp message every morning: VIP mail first, who still owes you a reply, follow-ups, today's meetings and a short digest of the rest.")}</p>
        {!linked && (
          <p data-testid="wa-brief-unlinked-hint" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
            {t("brief.unlinked_hint", "Briefs are delivered on WhatsApp — link your number above to receive them. You can still configure and preview below.")}
          </p>
        )}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-500">{t("brief.send_at", "Send at")}</span>
          <input data-testid="wa-brief-time" type="time" value={brief.time}
            onChange={(e) => saveBrief({ ...brief, time: e.target.value })}
            className="border border-gray-300 rounded px-2 py-1 text-xs" />
          <span className="text-[11px] text-gray-400">{t("brief.tz_time", "({tz} time)", { tz: tzLabel })}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {BRIEF_SECTIONS.map(([k, tk, label]) => (
            <label key={k} data-testid={`wa-brief-inc-${k}`}
              className={`flex items-center gap-1 text-[11px] font-medium rounded-full border px-2.5 py-1 cursor-pointer transition-colors ${brief.include[k] ? "bg-green-50 border-green-300 text-green-800" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
              <input type="checkbox" className="hidden" checked={!!brief.include[k]}
                onChange={() => saveBrief({ ...brief, include: { ...brief.include, [k]: !brief.include[k] } })} />
              {brief.include[k] ? "✓ " : ""}{t(tk, label)}
            </label>
          ))}
        </div>
        <button data-testid="wa-brief-preview-btn" onClick={preview} disabled={previewBusy}
          className="text-xs font-semibold border border-green-600 text-green-700 hover:bg-green-50 rounded-md px-3 py-1.5 disabled:opacity-50 transition-colors">
          {previewBusy ? t("brief.building", "Building preview…") : t("brief.preview_btn", "Send me a preview now")}
        </button>
        {previewText && (
          <pre data-testid="wa-brief-preview-text" className="mt-2 text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">{previewText}</pre>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input data-testid="wa-vip-alerts-toggle" type="checkbox" checked={vipAlerts}
            onChange={(e) => saveVip(e.target.checked)} className="w-4 h-4 accent-green-600" />
          {t("wa.vip_alerts", "👑 VIP instant alerts — ping me on WhatsApp the moment a VIP emails me")}
        </label>
      </div>
      <SharedAlertToggles />
    </div>
  );
};
